import React, { useState, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import axios from 'axios';
import { APIURL } from '../../config';
import moment from 'moment';
import './HomeDashboardKPIs.css';

const kpiConfig = [
    {
        key: 'activeCases',
        label: 'Open Shifts',
        icon: 'fas fa-briefcase-medical',
        cardClass: 'kpi-active-cases',
        iconClass: 'icon-active-cases',
        prefix: '',
        suffix: '',
    },
    {
        key: 'unassignedVisits',
        label: 'Unassigned Shifts',
        icon: 'fas fa-user-slash',
        cardClass: 'kpi-unassigned-visits',
        iconClass: 'icon-unassigned-visits',
        prefix: '',
        suffix: '',
    },
    {
        key: 'caregiverUtilization',
        label: 'Caregiver Utilization',
        icon: 'fas fa-user-clock',
        cardClass: 'kpi-caregiver-utilization',
        iconClass: 'icon-caregiver-utilization',
        prefix: '',
        suffix: '',
    },
    {
        key: 'revenueAtRisk',
        label: 'Revenue at Risk',
        icon: 'fas fa-exclamation-triangle',
        cardClass: 'kpi-revenue-at-risk',
        iconClass: 'icon-revenue-at-risk',
        prefix: '$',
        suffix: '',
    },
];

export default function HomeDashboardKPIs(props) {
    const [isLoading, setIsLoading] = useState(true);
    const [kpiData, setKpiData] = useState({
        activeCases: 0,
        unassignedVisits: 0,
        caregiverUtilization: 0,
        revenueAtRisk: 0,
    });

    useEffect(() => {
        fetchKPIs();
    }, []);

    const fetchKPIs = async () => {
        try {
            setIsLoading(true);

            const now = moment();
            const today = now.format('YYYY-MM-DD');
            const weekEnd = moment().add(7, 'days').format('YYYY-MM-DD');

            // All 3 fired in parallel; caregiverCount is a tiny COUNT(*) query
            const [schedulesRes, caregiverCountRes, preAuthRes] = await Promise.all([
                axios.get(`${APIURL}/master_schedule/company/${props.comp_id}`, {
                    params: { startDate: today, endDate: weekEnd }
                }),
                axios.get(`${APIURL}/employees/caregiverCount`, {
                    params: { com_id: props.comp_id }
                }),
                axios.get(`${APIURL}/preAuth`, {
                    params: { companyID: props.comp_id, primaryBusinessType: 103, startDate: today, endDate: weekEnd }
                }).catch(() => ({ data: { rows: [] } })),
            ]);

            const schedules = schedulesRes.data || [];
            const totalCaregivers = caregiverCountRes.data?.count || 0;

            // Split schedule IDs: active-now (for active cases) vs all-week (for revenue at risk)
            const activeNowIds = schedules
                .filter(sch => moment(sch.start_time).isSameOrBefore(now) && moment(sch.end_time).isSameOrAfter(now))
                .map(sch => sch.schedule_id).filter(Boolean);

            const weekIds = schedules.map(sch => sch.schedule_id).filter(Boolean);

            // Fetch both session sets in parallel — lite endpoint skips JOINs/CTEs not needed here
            const [activeSessionsRes, weekSessionsRes] = await Promise.all([
                activeNowIds.length > 0
                    ? axios.post(`${APIURL}/sessions/scheduleLite`, { scheduleIds: activeNowIds })
                    : Promise.resolve({ data: [] }),
                weekIds.length > 0
                    ? axios.post(`${APIURL}/sessions/scheduleLite`, { scheduleIds: weekIds })
                    : Promise.resolve({ data: [] }),
            ]);

            const activeSessions = activeSessionsRes.data || [];
            const weekSessions = weekSessionsRes.data || [];

            // 1. Active Cases — distinct customers in currently active shifts
            const activeCustomerIds = new Set(
                activeSessions.map(s => s.customer_id).filter(Boolean)
            );

            // 2. Unassigned Shifts — current/future shifts with no employee assigned
            const unassigned = schedules.filter((sch) => {
                if (!moment(sch.end_time).isSameOrAfter(now)) return false;
                const empIds = sch.emp_id || sch.employee_ids || [];
                return (Array.isArray(empIds) ? empIds : []).length === 0;
            }).length;

            // 3. Caregiver Utilization — uses COUNT from backend, emp IDs from schedules
            const scheduledEmpIds = new Set();
            schedules.forEach((sch) => {
                if (!moment(sch.end_time).isSameOrAfter(now)) return;
                const empIds = sch.emp_id || sch.employee_ids || [];
                const empArr = Array.isArray(empIds) ? empIds : [empIds];
                empArr.forEach((id) => { if (id) scheduledEmpIds.add(id); });
            });
            const utilization = totalCaregivers > 0
                ? Math.round((scheduledEmpIds.size / totalCaregivers) * 100)
                : 0;

            // 4. Revenue at Risk — authorizations expiring this week with no shifts planned
            const weekScheduledCustomerIds = new Set(
                weekSessions.map(s => s.customer_id).filter(Boolean)
            );
            const preAuthRows = preAuthRes.data?.rows || [];
            const preAuths = Array.isArray(preAuthRows) ? preAuthRows : [];
            const revenueAtRisk = preAuths.reduce((sum, pa) => {
                const endDate = pa.endDate || pa.end_date;
                if (!endDate) return sum;
                if (!moment(endDate).isBetween(moment(today), moment(weekEnd), null, '[]')) return sum;
                const cusId = pa.customer_id || pa.cus_id;
                if (cusId && weekScheduledCustomerIds.has(cusId)) return sum;
                return sum + (parseFloat(pa.authAmount) || 0);
            }, 0);

            setKpiData({
                activeCases: activeCustomerIds.size,
                unassignedVisits: unassigned,
                caregiverUtilization: utilization,
                revenueAtRisk: Math.round(revenueAtRisk * 100) / 100,
            });
        } catch (error) {
            console.error('Error fetching dashboard KPIs:', error);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="home-kpi-loading">
                <CircularProgress size={32} />
            </div>
        );
    }

    return (
        <div className="home-kpi-container">
            {kpiConfig.map((kpi) => (
                <div key={kpi.key} className={`home-kpi-card ${kpi.cardClass}`}>
                    <div className="home-kpi-header">
                        <div className={`home-kpi-icon ${kpi.iconClass}`}>
                            <i className={kpi.icon}></i>
                        </div>
                        <span className="home-kpi-label">{kpi.label}</span>
                    </div>
                    <div className="home-kpi-value">
                        {kpi.prefix}{(kpiData[kpi.key] ?? 0).toLocaleString()}{kpi.suffix}
                    </div>
                </div>
            ))}
        </div>
    );
}
